import React, { useEffect, useId, useRef, useState } from 'react';

import type { BlobStyleProps, MetaballColors } from './types';

const DEFAULT_METABALL_COLORS: MetaballColors = {
  from: '#FFAE00',
  to: '#FF2D75',
};

/**
 * Metaball blob — SVG gooey filter merging multiple moving circles into a
 * single organic shape. Ported from the wiki mock.
 */
export const MetaballBlob: React.FC<BlobStyleProps & { colors?: MetaballColors }> = ({ level, size, colors }) => {
  const t = useRef(0);
  const [circles, setCircles] = useState<{ cx: number; cy: number; r: number }[]>([]);

  useEffect(() => {
    let raf: number;
    const N = 5;
    const tick = () => {
      t.current += 0.015;
      const c: { cx: number; cy: number; r: number }[] = [];
      for (let i = 0; i < N; i++) {
        const a = (i / N) * Math.PI * 2 + t.current * 0.4;
        const r = 12 + level * 14;
        const dist = 14 + Math.sin(t.current * 1.2 + i) * 6 + level * 12;
        c.push({
          cx: 50 + Math.cos(a) * dist,
          cy: 50 + Math.sin(a) * dist,
          r,
        });
      }
      c.push({ cx: 50, cy: 50, r: 18 + level * 8 });
      setCircles(c);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [level]);

  const c = colors ?? DEFAULT_METABALL_COLORS;
  // Unique ids so multiple MetaballBlob instances on the same page (e.g.
  // the studio gallery) don't share `<defs>` and collide.
  const uid = useId().replace(/:/g, '');
  const filterId = `voice-meta-goo-${uid}`;
  const gradId = `voice-meta-g-${uid}`;

  return (
    <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden>
      <defs>
        <filter id={filterId}>
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.2" result="blur" />
          <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8" />
        </filter>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={c.from} />
          <stop offset="100%" stopColor={c.to} />
        </linearGradient>
      </defs>
      <g filter={`url(#${filterId})`}>
        {circles.map((circ, i) => (
          <circle key={i} cx={circ.cx} cy={circ.cy} r={circ.r} fill={`url(#${gradId})`} />
        ))}
      </g>
    </svg>
  );
};

export default MetaballBlob;
